"use client";

import type { FC } from "react";
import { Card, Icon, useTheme, type IconName } from "@huub-web/ui";
import { mergeClasses } from "@huub-web/utils";

import type { IllustrationName } from "../../types/IllustrationType";

import { illustrationContainerStyle } from "./Illustration.css";
import {
  whitelabelInnerCardStyle,
  whitelabelCardContentStyle,
  whitelabelIconStyle,
  whitelabelCardStyle,
  illustrationStyle,
} from "./WhitelabelIllustration.css";
import type { IllustrationProps } from "./Illustration";

export type WhitelabelIllustrationProps = IllustrationProps;

/**
 * Maps illustration names to appropriate icon names
 */
const illustrationToIconMap: Partial<Record<IllustrationName, IconName>> = {
  maintenance: "certificate",
  calculate: "calculator",
  calculating: "calculator",
  calculation: "calculator",
  foundation: "wall",
  dashboard: "formPlus",
  phoneContact: "call",
  houseLove: "house",
  interest: "graph",
  interestThought: "graph",
  advisorGrowth: "person",
  advisorCompliant: "certificate",
  advisorChill: "person",
  advisor: "person",
  certificate: "certificate",
  notFound: "help",
  together: "people",
  houseAnalysis: "houseSearch",
  houseScan: "houseSearch",
  housesThought: "house",
  myHouse: "house",
  refinance: "mortgage",
  building: "apartment",
  shrug: "help",
  living: "house",
  outside: "house",
  price: "euro",
  neighbourhood: "neighbourhood",
  kitchen: "house",
  sun: "sunPositionFilled",
  technology: "greenHouse",
  materials: "wall",
  clipboard: "documentText",
  map: "map",
  default: "compass",
};

/**
 * Gets the icon name for a given illustration name
 */
const getIconForIllustration = (name: IllustrationName): IconName =>
  illustrationToIconMap[name] || "compass";

/**
 * Whitelabel themed Illustration component.
 * Displays a card with a centered icon that matches the illustration name.
 */
export const WhitelabelIllustration: FC<WhitelabelIllustrationProps> = ({
  name,
  size,
  className,
  alt,
  alignSelf,
  style,
  ref,
}) => {
  const { theme } = useTheme();
  const iconName = getIconForIllustration(name);

  const cardTheme = theme !== "light" && theme !== "dark" ? theme : undefined;

  return (
    <figure
      ref={ref}
      className={mergeClasses(
        illustrationContainerStyle({
          size: "auto",
          alignSelf,
          loaded: true,
        }),
        illustrationStyle({ size }),
        className,
      )}
      role="img"
      aria-label={alt || name}
      style={style}
    >
      <Card
        variant="tertiary"
        padding="none"
        className={whitelabelCardStyle()}
        innerClassName={whitelabelInnerCardStyle({ theme: cardTheme })}
        contentClassName={whitelabelCardContentStyle({ size })}
      >
        <Icon icon={iconName} className={whitelabelIconStyle({ size })} />
      </Card>
    </figure>
  );
};
